// PostCommentAndLikesShareComponent.js
import { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  AvatarGroup,
  Menu,
  MenuItem,
  Snackbar,
} from "@mui/material";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmojiPicker from "emoji-picker-react";
import IconAndTextButton from "./IconAndTextButton";
import api from "../../config/axios";

function PostCommentAndLikesShareComponent({ post, onPostUpdate }) {
  const [commentText, setCommentText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // Use a safe default if post is not defined
  const safePost = post || { _id: "", likes: [], comments: [], shares: [] };
  const defaultAvatar = "/images/navbar-profile-pic.jpeg";

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const handleSocialShare = (platform) => {
    // Create the post URL using the post ID
    const postUrl = `${window.location.origin}/post/${safePost._id}`;
    let shareUrl = '';

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this post: ${postUrl}`)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent('Check out this post!')}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(postUrl).then(() => {
          setShowCopySuccess(true);
          setTimeout(() => setShowCopySuccess(false), 3000);
        });
        handleShareClose();
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    handleShareClose();

    // Call the backend to record the share
    handleShare();
  };

  const handleLike = async () => {
    try {
      const response = await api.post(`/posts/${safePost._id}/like`);
      if (onPostUpdate) {
        onPostUpdate(response.data.post);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      alert(error?.response?.data?.message || "Error toggling like");
    }
  };

  const handleShare = async () => {
    try {
      const response = await api.post(`/posts/${safePost._id}/share`);
      if (onPostUpdate) {
        onPostUpdate(response.data.post);
      }
    } catch (error) {
      console.error("Error toggling share:", error);
      alert(error?.response?.data?.message || "Error toggling share");
    }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) {
      alert("Please enter a comment.");
      return;
    }
    try {
      const response = await api.post(`/posts/${safePost._id}/comment`, { text: commentText });
      if (onPostUpdate) {
        onPostUpdate(response.data.post);
      }
      setCommentText("");
    } catch (error) {
      console.error("Error adding comment:", error);
      alert(error?.response?.data?.message || "Error adding comment");
    }
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
  };

  const onEmojiClick = (emojiData, event) => {
    setCommentText((prev) => prev + emojiData.emoji);
  };

  return (
    <Stack spacing={2} sx={{ py: "10px", position: "relative" }}>
      <Stack
        id="like-comment-share-btn-div"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={0.8} sx={{ alignItems: "center" }}>
          {safePost.likes && safePost.likes.length > 0 ? (
            <>
              <AvatarGroup max={4} sx={{
                '& .MuiAvatar-root': {
                  width: 24,
                  height: 24,
                  fontSize: '0.8rem',
                  border: '2px solid #fff'
                }
              }}>
                {safePost.likes.map((user, index) => (
                  <Avatar
                    key={user._id}
                    src={user.profileImage || defaultAvatar}
                    alt={`${user.firstName} ${user.lastName}`}
                  />
                ))}
              </AvatarGroup>
              <Typography variant="body2" sx={{ fontSize: "14px", pl: 1 }}>
                {safePost.likes.length} {safePost.likes.length === 1 ? 'Like' : 'Likes'}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "14px" }}>
              No likes yet
            </Typography>
          )}
          {safePost.shares && safePost.shares.length > 0 && (
            <Typography variant="body2" sx={{ fontSize: "14px", ml: 2 }}>
              • {safePost.shares.length} {safePost.shares.length === 1 ? 'Share' : 'Shares'}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          <IconAndTextButton onClick={handleLike} src="/icons/heart-emoji.svg">
            Like
          </IconAndTextButton>
          <IconAndTextButton src="/icons/commentIcon.svg">
            {safePost.comments?.length || 0} {safePost.comments?.length === 1 ? 'Comment' : 'Comments'}
          </IconAndTextButton>
          <IconAndTextButton 
            onClick={handleShareClick}
            src="/icons/shareIcon.svg"
          >
            Share
          </IconAndTextButton>
        </Stack>
      </Stack>

      {/* Share Menu */}
      <Menu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            }
          }
        }}
      >
        <MenuItem onClick={() => handleSocialShare('whatsapp')}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <WhatsAppIcon sx={{ color: '#25D366', fontSize: 24 }} />
            <Typography>Share on WhatsApp</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('facebook')}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <FacebookIcon sx={{ color: '#1877F2', fontSize: 24 }} />
            <Typography>Share on Facebook</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('twitter')}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <TwitterIcon sx={{ color: '#1DA1F2', fontSize: 24 }} />
            <Typography>Share on Twitter</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('copy')}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <ContentCopyIcon sx={{ color: '#666666', fontSize: 24 }} />
            <Typography>Copy Link</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      {/* Success Snackbar for Copy */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            bgcolor: '#4CAF50',
            color: 'white',
          }
        }}
      />

      <TextField
        placeholder="Write a comment"
        fullWidth
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
        sx={{
          input: {
            bgcolor: "#ffffff",
            color: "#000000",
            fontSize: "14px",
            boxSizing: "content-box",
            py: "10px",
            borderRadius: "50px",
            "&::placeholder": {
              color: "#000000",
              opacity: 1,
            },
          },
          "& .MuiOutlinedInput-root": {
            px: "8px",
            borderRadius: "50px",
            bgcolor: "#ffffff",
            "& fieldset": {
              borderColor: "#D9D9D9",
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: "#D9D9D9",
              borderWidth: "1px",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#D9D9D9",
              borderWidth: "1px",
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment sx={{ pr: "6px" }} position="start">
              <Avatar
                src="/images/likes-avatar-picture.jpeg"
                alt="Profile Picture"
                sx={{
                  width: 30,
                  height: 30,
                  objectFit: "cover",
                  mr: "4px",
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <Stack direction="row" spacing={0.5}>
              <IconButton sx={{ p: "0px" }}>
                <Box
                  component="img"
                  src="/icons/uploadIcon.svg"
                  sx={{
                    width: "16px",
                    height: "16px",
                    border: "1px solid #CBD5E1",
                    borderRadius: "50%",
                    p: "6px",
                  }}
                />
              </IconButton>
              <IconButton sx={{ p: "0px" }} onClick={toggleEmojiPicker}>
                <Box
                  component="img"
                  src="/icons/smileEmojiIcon.svg"
                  sx={{
                    width: "16px",
                    height: "16px",
                    border: "1px solid #CBD5E1",
                    borderRadius: "50%",
                    p: "6px",
                  }}
                />
              </IconButton>
              <Button
                variant="outlined"
                sx={{
                  py: "3.5px",
                  px: "16px",
                  bgcolor: "#000000",
                  color: "#ffffff",
                  textTransform: "none",
                  borderRadius: "50px",
                }}
                onClick={handleCommentSubmit}
              >
                Post
              </Button>
            </Stack>
          ),
        }}
      />
      {showEmojiPicker && (
        <Box sx={{ position: "absolute", bottom: "60px", right: "16px", zIndex: 10 }}>
          <EmojiPicker onEmojiClick={onEmojiClick} />
        </Box>
      )}
    </Stack>
  );
}

export default PostCommentAndLikesShareComponent;
